import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { camera, cameraControls, render, renderer, scene } from '@/threejs/core';
import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';

let isMeasuring = false;
let measurementLine = null;
let measurementPoints = [];
let selectableObjects = [];
let measurementLabel = null;
let measurementLabelObject = null;
let raycaster = new THREE.Raycaster();
let mouse = new THREE.Vector2();
let measurementOverlay = null;
let pointerDownPosition = null;
let tempMeasurement = null;
let cursorMarker = null;
let instructionText = null;
let groundPlane = null;
let activeGizmos = []; // Store endpoint gizmos
let selectedMeasurementId = null;
let selectedMeasurementObject = null;
let isDraggingGizmo = false;
let draggedGizmo = null;
let dragPlane = null;
const MOVEMENT_THRESHOLD = 15; // pixels
const SNAP_TO_VERTEX = false; // Set to true to snap to nearest vertex, false to use intersection point

export function isMeasurementActive() {
  return isMeasuring;
}

export function getMeasurementDistance() {
  if (measurementPoints.length === 2) {
    const distance = measurementPoints[0].distanceTo(measurementPoints[1]);
    return `${distance.toFixed(2)}m`;
  }
  return null;
}

export function setUpMeasurementHandler() {
  // Create measurement line material
  const lineMaterial = new LineMaterial({
    color: 0xffffff,
    linewidth: 2,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 1.0,
  });
  const lineGeometry = new LineGeometry();
  measurementLine = new Line2(lineGeometry, lineMaterial);
  measurementLine.visible = false; // Keep hidden until first point is set

  //scene.add(measurementLine)
  measurementLine.renderOrder = 1000;

  // Create measurement label
  measurementLabel = document.createElement('div');
  measurementLabel.style.color = 'white';
  measurementLabel.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
  measurementLabel.style.padding = '4px 8px';
  measurementLabel.style.borderRadius = '4px';
  measurementLabel.style.fontSize = '14px';
  measurementLabel.style.fontFamily = 'Arial';
  measurementLabel.style.pointerEvents = 'none';
  measurementLabel.style.display = 'none';

  // Create CSS2D object for the label
  measurementLabelObject = new CSS2DObject(measurementLabel);
  //scene.add(measurementLabelObject)

  // Create measurement overlay
  measurementOverlay = document.createElement('div');
  measurementOverlay.id = 'measurementOverlay';
  measurementOverlay.style.position = 'absolute';
  measurementOverlay.style.bottom = '10px';
  measurementOverlay.style.left = '50%';
  measurementOverlay.style.transform = 'translateX(-50%)';
  measurementOverlay.style.color = 'white';
  measurementOverlay.style.fontSize = '14px';
  measurementOverlay.style.fontFamily = 'Arial';
  measurementOverlay.style.padding = '8px 16px';
  measurementOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
  measurementOverlay.style.borderRadius = '4px';
  measurementOverlay.style.display = 'none';
  measurementOverlay.style.alignItems = 'center';
  measurementOverlay.style.gap = '12px';
  measurementOverlay.style.zIndex = '1000';
  measurementOverlay.style.color = '#98A2B3';
  instructionText = document.createElement('span');
  instructionText.textContent = 'Click on an object to start measuring';
  measurementOverlay.appendChild(instructionText);

  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.style.margin = '4px 8px';
  cancelButton.style.padding = '4px 8px';
  cancelButton.style.border = '1px solid white';
  cancelButton.style.borderRadius = '4px';
  cancelButton.style.borderColor = '#1D2A39';
  cancelButton.style.color = 'white';
  cancelButton.style.cursor = 'pointer';
  cancelButton.style.fontSize = '14px';
  cancelButton.style.backgroundColor = '#101828';
  cancelButton.style.color = '#98A2B3';
  cancelButton.addEventListener('click', stopMeasurement);
  measurementOverlay.appendChild(cancelButton);

  renderer.domElement.parentElement.appendChild(measurementOverlay);

  // Create cursor marker sphere
  const markerGeometry = new THREE.SphereGeometry(0.05, 16, 16);
  const markerMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ff00,
    transparent: true,
    opacity: 0.8,
    depthTest: false,
  });
  cursorMarker = new THREE.Mesh(markerGeometry, markerMaterial);
  cursorMarker.visible = false;
  cursorMarker.renderOrder = 999;
  scene.add(cursorMarker);

  // Create invisible ground plane for measurements in blank space
  const groundGeometry = new THREE.PlaneGeometry(10000, 10000);
  const groundMaterial = new THREE.MeshBasicMaterial({
    visible: false,
    side: THREE.DoubleSide,
  });
  groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
  groundPlane.rotation.x = -Math.PI / 2; // Rotate to be horizontal
  groundPlane.position.y = 0; // Place at y=0
  groundPlane.userData.isMeasurementGroundPlane = true;
  scene.add(groundPlane);

  // Handle pointer events with capture phase to intercept before orbit controls
  renderer.domElement.addEventListener('pointerdown', onPointerDown, {
    capture: true,
  });
  renderer.domElement.addEventListener('pointerup', onPointerUp, {
    capture: true,
  });
  renderer.domElement.addEventListener('pointermove', onPointerMove, {
    capture: true,
  });

  // Handle window resize
  window.addEventListener('resize', () => {
    lineMaterial.resolution.set(window.innerWidth, window.innerHeight);
  });

  return {
    startMeasurement,
    stopMeasurement,
    highLightMeasurement,
    removeGizmos,
  };
}

function onPointerDown(event) {
  if (isMeasuring) {
    pointerDownPosition = { x: event.clientX, y: event.clientY };
    return;
  }

  // Check if clicking on a gizmo FIRST before any other event handling
  const rect = renderer.domElement.getBoundingClientRect();
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  );

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(activeGizmos, true);

  if (intersects.length > 0) {
    // Find the gizmo group (parent with userData.isGizmoDraggable)
    let gizmoGroup = intersects[0].object;
    while (gizmoGroup && !gizmoGroup.userData.isGizmoDraggable) {
      gizmoGroup = gizmoGroup.parent;
    }

    if (gizmoGroup && gizmoGroup.userData.isGizmoDraggable) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();

      // Disable camera controls before setting drag state
      if (cameraControls) {
        cameraControls.enabled = false;
        if (cameraControls.domElement) {
          cameraControls.domElement.style.pointerEvents = 'none';
        }
      }

      isDraggingGizmo = true;
      draggedGizmo = gizmoGroup;

      // Create drag plane perpendicular to camera at gizmo position
      const planeNormal = new THREE.Vector3();
      camera.getWorldDirection(planeNormal);
      dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, draggedGizmo.position);

      return false;
    }
  }
}

function onPointerUp(event) {
  if (!isMeasuring || !pointerDownPosition) {
    // Handle gizmo drag end
    if (isDraggingGizmo) {
      event.stopPropagation();

      event.preventDefault();

      isDraggingGizmo = false;
      draggedGizmo = null;
      dragPlane = null;

      // Immediately re-enable camera controls
      if (cameraControls) {
        // Re-enable pointer events first
        if (cameraControls.domElement) {
          cameraControls.domElement.style.pointerEvents = '';
        }
        // Then re-enable controls
        cameraControls.enabled = true;
      }

      // Reset cursor and frame counters
      renderer.domElement.style.cursor = 'default';
      window._dragFrameCount = 0;
      window._pointerMoveCallCount = 0;

      return false;
    }
    return;
  }

  // Calculate the distance moved
  const dx = event.clientX - pointerDownPosition.x;
  const dy = event.clientY - pointerDownPosition.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Only handle measurement if movement was within threshold
  if (distance <= MOVEMENT_THRESHOLD) {
    handleMeasurementClick(event);
  }

  // Reset pointer down position
  pointerDownPosition = null;
}

export function handleMeasurementClick(event) {
  const rect = renderer.domElement.getBoundingClientRect();
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  );

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects([...selectableObjects, groundPlane], true);

  if (intersects.length > 0) {
    const intersect = intersects[0];
    const point = intersect.point;
    const mesh = intersect.object;

    let finalPoint = point.clone();

    if (SNAP_TO_VERTEX) {
      // Find the nearest vertex
      if (mesh.geometry && mesh.geometry.attributes.position) {
        const positions = mesh.geometry.attributes.position.array;
        let minDistance = Infinity;
        let nearestVertex = new THREE.Vector3();

        // Iterate through all vertices
        for (let i = 0; i < positions.length; i += 3) {
          const vertex = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
          // Transform vertex to world space
          vertex.applyMatrix4(mesh.matrixWorld);

          const distance = vertex.distanceTo(point);
          if (distance < minDistance) {
            minDistance = distance;
            nearestVertex.copy(vertex);
          }
        }
        finalPoint = nearestVertex;
      }
    } else {
      // Clamp Y to not go below 0
      if (finalPoint.y < 0) {
        finalPoint.y = 0;
      }
    }

    measurementPoints.push(finalPoint);

    if (measurementPoints.length === 1) {
      // Show the line now that we have the first point
      measurementLine.visible = true;
      // Update instruction text after first click
      instructionText.textContent = 'Click on another object to complete measurement';
    } else if (measurementPoints.length === 2) {
      createMeasurement();
      stopMeasurement();
    }
  }
}

function startMeasurement() {
  const centerPanel = document.getElementById('centerPanel');
  const measurementOverlay = document.getElementById('measurementOverlay');
  measurementOverlay.style.bottom = '10px';
  if (centerPanel && measurementOverlay) measurementOverlay.style.bottom = `${centerPanel.offsetHeight + 18}px`;

  selectableObjects = [];
  scene.traverse((object) => {
    if (object.userData.selectable === true) {
      selectableObjects.push(object);
    }
  });

  isMeasuring = true;
  measurementPoints = [];
  measurementLine.visible = false; // Don't show line until first point is set
  measurementLabel.style.display = 'none';
  measurementOverlay.style.display = 'block';
  instructionText.textContent = 'Click on an object to start measuring';
  cursorMarker.visible = true;
  render();
}

function stopMeasurement() {
  isMeasuring = false;
  measurementLine.visible = false;
  measurementLabel.style.display = 'none';
  measurementOverlay.style.display = 'none';
  measurementLabel.textContent = '';
  measurementPoints = [];
  cursorMarker.visible = false;
  if (tempMeasurement) {
    tempMeasurement = null;
  }
  render();
}

function createMeasurement() {
  const cruxModuleStore = useCruxModuleStore();
  const distance = getMeasurementDistance();

  cruxModuleStore.addSceneModule(
    null,
    'measurement',
    {
      points: measurementPoints,
      distance: distance,
    },
    `Measurement ${distance}`
  );
}

function onPointerMove(event) {
  // Handle gizmo dragging first
  if (isDraggingGizmo && draggedGizmo && dragPlane) {
    event.stopPropagation();

    event.preventDefault();

    renderer.domElement.style.cursor = 'grabbing';

    const rect = renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);

    const newPosition = new THREE.Vector3();

    if (newPosition) {
      raycaster.ray.intersectPlane(dragPlane, newPosition);
      draggedGizmo.position.copy(newPosition);

      // Update measurement
      const measurementObject = draggedGizmo.userData.measurementObject;
      const pointIndex = draggedGizmo.userData.pointIndex;
      updateMeasurementFromGizmo(measurementObject, pointIndex, newPosition);
    }
    return;
  }

  // Check for gizmo hover to change cursor
  if (!isMeasuring && activeGizmos.length > 0) {
    const rect = renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(activeGizmos, true); // recursive

    if (intersects.length > 0) {
      // Find the gizmo group (parent with userData.isGizmoDraggable)
      let gizmoGroup = intersects[0].object;
      while (gizmoGroup && !gizmoGroup.userData.isGizmoDraggable) {
        gizmoGroup = gizmoGroup.parent;
      }

      if (gizmoGroup && gizmoGroup.userData.isGizmoDraggable) {
        renderer.domElement.style.cursor = 'grab';
      } else {
        renderer.domElement.style.cursor = 'default';
      }
    } else {
      renderer.domElement.style.cursor = 'default';
    }
  }

  if (!isMeasuring) return;

  const rect = renderer.domElement.getBoundingClientRect();
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  );

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects([...selectableObjects, groundPlane], true);

  if (intersects.length > 0) {
    const intersect = intersects[0];
    const point = intersect.point;
    const mesh = intersect.object;

    let markerPosition = point.clone();

    if (SNAP_TO_VERTEX) {
      // Find the nearest vertex for cursor marker
      if (mesh.geometry && mesh.geometry.attributes.position) {
        const positions = mesh.geometry.attributes.position.array;
        let minDistance = Infinity;
        let nearestVertex = new THREE.Vector3();

        for (let i = 0; i < positions.length; i += 3) {
          const vertex = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
          vertex.applyMatrix4(mesh.matrixWorld);

          const distance = vertex.distanceTo(point);
          if (distance < minDistance) {
            minDistance = distance;
            nearestVertex.copy(vertex);
          }
        }
        markerPosition = nearestVertex;
      }
    } else {
      // Clamp Y to not go below 0
      if (markerPosition.y < 0) {
        markerPosition.y = 0;
      }
    }

    // Update cursor marker position
    cursorMarker.position.copy(markerPosition);
    cursorMarker.visible = true;

    // Only update temp measurement if first point is set
    if (measurementPoints.length === 1) {
      const tempPoints = [...measurementPoints, markerPosition];
      // Update the visual measurement line with temp points
      updateMeasurement(tempPoints);
    }
    render();
  } else {
    // Hide cursor marker if not hovering over any object
    cursorMarker.visible = false;
    render();
  }
}

export function cleanupMeasurementGizmos(measurementId) {
  // Only remove gizmos if they belong to the measurement being deleted
  if (selectedMeasurementId === measurementId) {
    removeGizmos();
  }
}

export function clearAllMeasurementGizmos() {
  removeGizmos();
}

export function highLightMeasurement(id) {
  // Force removal of all gizmos before highlighting
  removeGizmos();

  // Force a render to ensure gizmos are visually removed
  render();

  const selectionStore = useSelectionStore();
  selectionStore.selectedObjectTransformType = 'translate';
  selectionStore.selectedObjectTransformTarget = false;

  // Hide measurement overlay
  if (measurementOverlay) {
    measurementOverlay.style.display = 'none';
  }

  let foundMeasurement = false;
  let totalMeasurements = 0;

  scene.traverse((object) => {
    if (!object) return;

    // Check if this is a measurement object (has line property)
    if (!object.line) return;

    totalMeasurements++;

    if (object.userData.code === id) {
      foundMeasurement = true;

      const { material } = object.line;
      material.color.set('#ED6BFF');
      material.linewidth = 4;
      material.opacity = 1;
      material.transparent = true;
      object.renderOrder = 2000;

      // Store selection and add gizmos
      selectedMeasurementId = id;
      selectedMeasurementObject = object;

      // Set selection store
      selectionStore.selectedObjectId = id;

      addGizmosToMeasurement(object);
    } else if (object.userData.type === 'measurement') {
      // Reset other measurements to default style
      const { material } = object.line;
      material.color.set('#FFFFFF');
      material.linewidth = 2;
      material.opacity = 1;
      material.transparent = true;
      object.renderOrder = 1000;
    }
  });

  render();
}
function addGizmosToMeasurement(measurementObject) {
  if (!measurementObject || !measurementObject.userData.points) {
    return;
  }

  const points = measurementObject.userData.points;

  if (!Array.isArray(points) || points.length !== 2) {
    return;
  }

  // Convert points to Vector3 if they're plain objects
  const vector3Points = points.map((p, idx) => {
    if (p instanceof THREE.Vector3) {
      return p;
    } else if (p && typeof p.x === 'number' && typeof p.y === 'number' && typeof p.z === 'number') {
      return new THREE.Vector3(p.x, p.y, p.z);
    } else {
      return null;
    }
  });

  if (vector3Points.includes(null)) {
    return;
  }

  // Purge any existing gizmos from the scene before adding new ones
  const orphans = scene.children.filter((c) => c.userData && c.userData.isGizmoDraggable);
  orphans.forEach((gizmo) => {
    scene.remove(gizmo);
    gizmo.children.forEach((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  });
  activeGizmos.length = 0;

  // Create gizmos for both endpoints
  createEndpointGizmo(vector3Points[0], measurementObject, 0);
  createEndpointGizmo(vector3Points[1], measurementObject, 1);
}

function createEndpointGizmo(position, measurementObject, pointIndex) {
  // Ensure position is a valid Vector3
  if (!position || !(position instanceof THREE.Vector3)) {
    return;
  }

  // Create a group for the gizmo with multiple visual indicators
  const gizmoGroup = new THREE.Group();
  gizmoGroup.position.copy(position);

  // Inner solid sphere
  const sphereGeometry = new THREE.SphereGeometry(0.15, 16, 16);
  const sphereMaterial = new THREE.MeshBasicMaterial({
    color: 0xed6bff,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
  });
  const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
  gizmoGroup.add(sphere);

  // Outer ring to indicate draggability
  const ringGeometry = new THREE.RingGeometry(0.18, 0.22, 32);
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0xed6bff,
    transparent: true,
    opacity: 0.6,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.lookAt(camera.position);
  gizmoGroup.add(ring);

  gizmoGroup.renderOrder = 2001;
  gizmoGroup.userData.isGizmoDraggable = true;
  gizmoGroup.userData.measurementObject = measurementObject;
  gizmoGroup.userData.pointIndex = pointIndex;

  scene.add(gizmoGroup);
  activeGizmos.push(gizmoGroup);

  render();
}

function updateMeasurementFromGizmo(measurementObject, pointIndex, newPosition) {
  if (!measurementObject.userData.points) {
    return;
  }

  // Clone points array and convert to Vector3 if needed
  const updatedPoints = measurementObject.userData.points.map((p) =>
    p instanceof THREE.Vector3 ? p.clone() : new THREE.Vector3(p.x, p.y, p.z)
  );

  // Update the specific point
  updatedPoints[pointIndex].copy(newPosition);

  // Update visual
  if (measurementObject.updatePoints) {
    measurementObject.updatePoints(updatedPoints);
  }

  // Persist to store
  const cruxModuleStore = useCruxModuleStore();
  if (selectedMeasurementId) {
    const module = cruxModuleStore.cruxModules.find((m) => m.code === selectedMeasurementId);

    if (module) {
      // Calculate new distance
      const distance = updatedPoints[0].distanceTo(updatedPoints[1]);
      const distanceStr = `${distance.toFixed(2)}m`;

      const updatedModule = {
        ...module,
        name: `Measurement ${distanceStr}`,
        data: {
          ...module.data,
          points: updatedPoints.map((p) => ({ x: p.x, y: p.y, z: p.z })),
          distance: distanceStr,
        },
      };
      cruxModuleStore.update(selectedMeasurementId, updatedModule);
    }
  }

  render();
}

function removeGizmos() {
  // Clear activeGizmos array and remove from scene
  while (activeGizmos.length > 0) {
    const gizmo = activeGizmos.pop();

    // Remove from scene
    if (scene.children.includes(gizmo)) {
      scene.remove(gizmo);
    }

    // Dispose children
    if (gizmo.children) {
      gizmo.children.forEach((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    if (gizmo.geometry) gizmo.geometry.dispose();
    if (gizmo.material) gizmo.material.dispose();
  }

  // Also find and remove any orphaned gizmos in the scene by checking children directly
  const childrenToRemove = scene.children.filter((child) => child.userData && child.userData.isGizmoDraggable);

  childrenToRemove.forEach((gizmo) => {
    scene.remove(gizmo);
    // Dispose children
    if (gizmo.children) {
      gizmo.children.forEach((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    if (gizmo.geometry) gizmo.geometry.dispose();
    if (gizmo.material) gizmo.material.dispose();
  });

  // Clear all related state
  activeGizmos = [];
  selectedMeasurementId = null;
  selectedMeasurementObject = null;
  isDraggingGizmo = false;
  draggedGizmo = null;

  // Clean up drag plane
  if (dragPlane) {
    scene.remove(dragPlane);
    if (dragPlane.geometry) dragPlane.geometry.dispose();
    if (dragPlane.material) dragPlane.material.dispose();
    dragPlane = null;
  }

  // Reset cursor
  renderer.domElement.style.cursor = 'default';

  render();
}

function updateMeasurement(points = measurementPoints) {
  if (points.length === 0) return;

  // Update line geometry
  const positions = new Float32Array(points.length * 3);
  points.forEach((point, i) => {
    positions[i * 3] = point.x;
    positions[i * 3 + 1] = point.y;
    positions[i * 3 + 2] = point.z;
  });
  measurementLine.geometry.setPositions(positions);
  measurementLine.computeLineDistances();

  // Update measurement text
  if (points.length === 2) {
    const distance = points[0].distanceTo(points[1]);
    const midPoint = new THREE.Vector3().addVectors(points[0], points[1]).multiplyScalar(0.5);

    // Update the label text and position
    measurementLabel.textContent = `${distance.toFixed(2)}m`;
    measurementLabel.style.display = 'block';
    measurementLabelObject.position.copy(midPoint);
  }

  render();
}

export function cleanupMeasurementHandler() {
  // Remove active gizmos
  removeGizmos();

  if (measurementLine) {
    scene.remove(measurementLine);
    measurementLine.geometry.dispose();
    measurementLine.material.dispose();
  }
  if (measurementLabelObject) {
    scene.remove(measurementLabelObject);
  }
  if (cursorMarker) {
    scene.remove(cursorMarker);
    cursorMarker.geometry.dispose();
    cursorMarker.material.dispose();
  }
  if (groundPlane) {
    scene.remove(groundPlane);
    groundPlane.geometry.dispose();
    groundPlane.material.dispose();
  }
  if (measurementOverlay) {
    measurementOverlay.remove();
  }
  window.removeEventListener('resize', () => {
    measurementLine.material.resolution.set(window.innerWidth, window.innerHeight);
  });
  renderer.domElement.removeEventListener('pointerdown', onPointerDown, {
    capture: true,
  });
  renderer.domElement.removeEventListener('pointerup', onPointerUp, {
    capture: true,
  });
  renderer.domElement.removeEventListener('pointermove', onPointerMove, {
    capture: true,
  });
}
